<template>
    <div class="filters-container">
        <div class="row g-3 align-items-end">
            <div class="col-lg-4 col-md-6">
                <label for="auditoriaQ" class="form-label fw-semibold">
                    <i class="fas fa-search text-secondary me-1"></i>
                    Buscar
                </label>
                <input
                    id="auditoriaQ"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="URL, correo, mensaje o datos enviados..."
                    :value="filtros.q"
                    @input="actualizar('q', $event.target.value)"
                    @keyup.enter="$emit('search')"
                />
            </div>

            <div class="col-lg-4 col-md-6">
                <label for="auditoriaAdmin" class="form-label fw-semibold">
                    <i class="fas fa-user-shield text-secondary me-1"></i>
                    Admin
                </label>
                <select
                    id="auditoriaAdmin"
                    class="form-select form-select-sm"
                    :value="filtros.admin_user_id ?? ''"
                    @change="actualizar('admin_user_id', $event.target.value)"
                >
                    <option value="">Todos</option>
                    <option v-for="admin in admins" :key="admin.id" :value="admin.id">
                        {{ admin.email }}{{ admin.role_nombre ? ` — ${admin.role_nombre}` : '' }}{{ admin.es_admin_actual ? '' : ' (ya no es admin)' }}
                    </option>
                </select>
            </div>

            <div class="col-lg-2 col-md-6">
                <label for="auditoriaDesde" class="form-label fw-semibold">
                    <i class="fas fa-calendar-alt text-secondary me-1"></i>
                    Desde
                </label>
                <input
                    id="auditoriaDesde"
                    type="date"
                    class="form-control form-control-sm"
                    :value="filtros.desde"
                    @change="actualizar('desde', $event.target.value)"
                />
            </div>

            <div class="col-lg-2 col-md-6">
                <label for="auditoriaHasta" class="form-label fw-semibold">Hasta</label>
                <input
                    id="auditoriaHasta"
                    type="date"
                    class="form-control form-control-sm"
                    :value="filtros.hasta"
                    @change="actualizar('hasta', $event.target.value)"
                />
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="auditoriaMetodo" class="form-label fw-semibold">Método</label>
                <select
                    id="auditoriaMetodo"
                    class="form-select form-select-sm"
                    :value="filtros.metodo ?? ''"
                    @change="actualizar('metodo', $event.target.value)"
                >
                    <option value="">Todos</option>
                    <option v-for="metodo in metodos" :key="metodo" :value="metodo">{{ metodo }}</option>
                </select>
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="auditoriaExito" class="form-label fw-semibold">Resultado</label>
                <select
                    id="auditoriaExito"
                    class="form-select form-select-sm"
                    :value="filtros.exito ?? ''"
                    @change="actualizar('exito', $event.target.value)"
                >
                    <option value="">Todos</option>
                    <option value="1">Exitosas</option>
                    <option value="0">Con error (≥ 400)</option>
                </select>
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="auditoriaImpersonando" class="form-label fw-semibold">Impersonación</label>
                <select
                    id="auditoriaImpersonando"
                    class="form-select form-select-sm"
                    :value="filtros.impersonando ?? ''"
                    @change="actualizar('impersonando', $event.target.value)"
                >
                    <option value="">Todas</option>
                    <option value="1">Solo impersonando</option>
                    <option value="0">Solo directas</option>
                </select>
            </div>

            <div class="col-lg-3 col-md-6">
                <label for="auditoriaRuta" class="form-label fw-semibold">Ruta</label>
                <input
                    id="auditoriaRuta"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Ej. inmuebles"
                    :value="filtros.ruta"
                    @input="actualizar('ruta', $event.target.value)"
                    @keyup.enter="$emit('search')"
                />
            </div>

            <div class="col-lg-3 col-md-6">
                <label for="auditoriaEvento" class="form-label fw-semibold">Evento</label>
                <input
                    id="auditoriaEvento"
                    type="text"
                    class="form-control form-control-sm"
                    list="auditoriaEventos"
                    placeholder="Ej. auditoria.acceso_denegado"
                    :value="filtros.evento"
                    @input="actualizar('evento', $event.target.value)"
                    @keyup.enter="$emit('search')"
                />
                <datalist id="auditoriaEventos">
                    <option value="auditoria.acceso_denegado"></option>
                </datalist>
            </div>

            <div class="col-lg-3 col-md-4">
                <label for="auditoriaTabla" class="form-label fw-semibold">
                    <i class="fas fa-table text-secondary me-1"></i>
                    Tabla modificada
                </label>
                <select
                    id="auditoriaTabla"
                    class="form-select form-select-sm"
                    :value="filtros.tabla ?? ''"
                    @change="actualizar('tabla', $event.target.value)"
                >
                    <option value="">Todas</option>
                    <option v-for="t in opcionesTablas" :key="t.tabla" :value="t.tabla">
                        {{ t.tabla }}{{ t.total_cambios != null ? ` (${t.total_cambios})` : '' }}
                    </option>
                </select>
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="auditoriaRegistro" class="form-label fw-semibold">ID del registro</label>
                <input
                    id="auditoriaRegistro"
                    type="text"
                    class="form-control form-control-sm"
                    :placeholder="filtros.tabla ? 'Ej. 123' : 'Elige primero la tabla'"
                    :disabled="!filtros.tabla"
                    :value="filtros.registro_id"
                    @input="actualizar('registro_id', $event.target.value)"
                    @keyup.enter="$emit('search')"
                />
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="auditoriaOperacion" class="form-label fw-semibold">Tipo de cambio</label>
                <select
                    id="auditoriaOperacion"
                    class="form-select form-select-sm"
                    :value="filtros.operacion ?? ''"
                    @change="actualizar('operacion', $event.target.value)"
                >
                    <option value="">Todos</option>
                    <option value="crear">Crear</option>
                    <option value="editar">Editar</option>
                    <option value="borrar">Borrar</option>
                </select>
            </div>
        </div>

        <div class="actions-row">
            <div class="d-flex gap-2 flex-wrap align-items-center">
                <SoftButton color="primary" size="sm" variant="gradient" @click="$emit('search')">
                    <i class="fas fa-search me-1"></i>
                    Buscar
                </SoftButton>

                <SoftButton color="secondary" size="sm" variant="outline" @click="$emit('clear')">
                    <i class="fas fa-broom me-1"></i>
                    Limpiar
                </SoftButton>

                <SoftButton color="secondary" size="sm" variant="outline" @click="$emit('refresh')">
                    <i class="fas fa-sync-alt me-1"></i>
                    Actualizar
                </SoftButton>

                <span v-if="activeFiltersCount" class="badge bg-primary bg-gradient ms-2">
                    <i class="fas fa-filter me-1"></i>
                    {{ activeFiltersCount }} filtro{{ activeFiltersCount > 1 ? 's' : '' }}
                </span>

                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script>
import SoftButton from '@/components/SoftButton.vue';

export default {
    name: 'FiltersAuditoria',
    components: { SoftButton },
    props: {
        filtros: { type: Object, required: true },
        admins: { type: Array, default: () => [] },
        tablas: { type: Array, default: () => [] }
    },
    emits: ['update:filtros', 'search', 'clear', 'refresh'],
    data() {
        return { metodos: ['POST', 'PUT', 'PATCH', 'DELETE', 'GET'] };
    },
    computed: {
        // Si llega una tabla por "filtrar por registro" que no está en la lista, igual se muestra
        opcionesTablas() {
            if (!this.filtros.tabla || this.tablas.some((t) => t.tabla === this.filtros.tabla)) {
                return this.tablas;
            }
            return [...this.tablas, { tabla: this.filtros.tabla }];
        },
        activeFiltersCount() {
            return Object.values(this.filtros).filter((v) => v !== null && v !== '').length;
        }
    },
    methods: {
        actualizar(clave, valor) {
            const nuevos = { ...this.filtros, [clave]: valor === '' ? null : valor };
            // registro_id solo tiene sentido junto con la tabla
            if (clave === 'tabla' && !nuevos.tabla) nuevos.registro_id = null;
            this.$emit('update:filtros', nuevos);
        }
    }
};
</script>

<style lang="scss" scoped>
.filters-container {
    background: #f8f9fa;
    border-radius: 0.75rem;
    padding: 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;

    .form-label {
        margin-bottom: 0.4rem;
        color: #344767;
        font-size: 0.8rem;
    }
}

.actions-row {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}
</style>
